import { createSelector, createSlice, nanoid } from '@reduxjs/toolkit';
import { RootState } from 'js/store';
import { getStudentById } from '../students/studentSlice';
import { getGoalById, Goal, selectGoals } from '../goals/goalSlice';
import { getClassNameById } from '../students/classSlice';
import { groupBy } from 'js/helpers/array';

type Rating = {
  id: string;
  studentId: string;
  goalId: string;
  value: number;
  maxValue: number;
  presented: boolean;
  timestamp: number;
};

export type Ratings = Rating[];

export const initialState: Ratings = [];

export const ratingSlice = createSlice({
  name: 'ratings',
  initialState,
  reducers: {
    setStudentGoalRating: (state, { payload }) => {
      const { studentId, goalId, value, maxValue, presented } = payload;
      const newRating = {
        id: nanoid(),
        studentId,
        goalId,
        value,
        maxValue,
        presented,
        timestamp: Date.now(),
      };
      state.push(newRating);
    },
    deleteStudentRatings: (state, { payload }) => {
      const { studentIds } = payload;
      studentIds.forEach((studentId: string) => {
        const studentRatings = state.filter(
          (rating) => rating.studentId === studentId,
        );
        studentRatings.forEach((rating) => {
          const index = state.indexOf(rating);
          if (index > -1) {
            state.splice(index, 1);
          }
        });
      });
    },
  },
});

export const { setStudentGoalRating, deleteStudentRatings } =
  ratingSlice.actions;

// Selectors
export const selectRatings = (state: RootState) => state.ratings;

export const getLastRatingByStudentAndGoal = createSelector(
  [
    selectRatings,
    (_: RootState, studentId: string) => studentId,
    (_: RootState, _studentId: string, goalId: string) => goalId,
  ],
  (ratings, studentId, goalId) => {
    const sortedRatings = findRatingsByStudentAndGoal(
      ratings,
      studentId,
      goalId,
    )
      .filter((rating) => rating.presented !== true)
      .sort((a, b) => b.timestamp - a.timestamp);
    return sortedRatings.length > 0 ? sortedRatings[0] : { value: 0 };
  },
);

export const getNumPresentedByStudentAndGoal = createSelector(
  [
    selectRatings,
    (_: RootState, studentId: string) => studentId,
    (_: RootState, _studentId: string, goalId: string) => goalId,
  ],
  (ratings, studentId, goalId) => {
    const presentedRatings = findRatingsByStudentAndGoal(
      ratings,
      studentId,
      goalId,
    ).filter((rating) => rating.presented);

    return presentedRatings.length;
  },
);

const sumRatings = (previous, current) => {
  const { value: currVal = 0 } = current;
  return previous + currVal;
};

export const getLastRatedGoalIds = (ratings: Rating[]) => {
  const sortedRatings = [...ratings].sort((a, b) => b.timestamp - a.timestamp);

  return [...new Set(sortedRatings.map((rating) => rating.goalId))].slice(0, 4);
};

export const getStudentRatings = createSelector(
  [selectRatings, (_: RootState, studentId: string) => studentId],
  (ratings, studentId) => {
    return ratings.filter((rating) => rating.studentId === studentId);
  },
);

type GroupedRatings = Array<{id:string, name: string, maxValue: number, ratings:Array<Rating>}>;

export const getStudentRatingsGroupedByGoal = createSelector(
  [getStudentRatings, selectGoals],
  (studentRatings, goals):GroupedRatings => {
    // Wait for goals to fetch successfully
    if(!goals.data.length) return null;
    const grouped = groupBy(studentRatings, 'goalId');
    return Object.entries(grouped).map(([goalId, ratingsGroup]) => {
      const goal = goals.data.find((g) => g.id === goalId);
      return {
        id: goalId,
        name: goal?.publisherGoalLabel || '',
        maxValue: goal?.maxValue || 0,
        ratings: ratingsGroup as Rating[],
      };
    });
  },
);

const findRatingsByStudentAndGoal = (
  ratings: Ratings,
  studentId: string,
  goalId: string,
): Rating[] => {
  return (
    ratings.filter(
      (rating) => rating.studentId === studentId && rating.goalId === goalId,
    ) || []
  );
};

const sortByNameAndDate = (a: Array<any>, b: Array<any>) => {
  const { 0: nameA, 6: dateA } = a;
  const { 0: nameB, 6: dateB } = b;
  // Sort by name first, then by date
  // If names are equal, sort by date
  if (nameA.toUpperCase() === nameB.toUpperCase()) {
    return dateA.timestamp - dateB.timestamp;
  }
  if (nameA.toUpperCase() > nameB.toUpperCase()) {
    return 1;
  }
  if (nameA.toUpperCase() < nameB.toUpperCase()) {
    return -1;
  }
  return 0;
};

export const getRatingsAsCSV = createSelector(
  [(state) => state, selectRatings],
  (state, ratings) => {
    const header = [
      'Leerling',
      'Groep',
      'Doelenset',
      'Vak',
      'Doel',
      'Score',
      'MaxScore',
      'Aangeboden',
      'Datum',
    ];
    const rows = [...ratings]
      .map((rating) => {
        const student = getStudentById(state, rating.studentId);
        const goal = getGoalById(state, rating.goalId) || <Goal>{};
        const className = getClassNameById(state, student.classId);
        return [
          student.name || '',
          className || '',
          goal.publisherName || '',
          goal.domain || '',
          goal.publisherGoalLabel || '',
          rating.value || '',
          rating.maxValue || '',
          `${rating.presented ? 'ja' : 'nvt'}`,
          new Date(rating.timestamp || '').toLocaleDateString('nl-NL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }),
        ];
      })
      .sort(sortByNameAndDate)
      .map((row) =>
        row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','),
      );
    return [header.join(','), ...rows].join('\n');
  },
);

export default ratingSlice.reducer;
